import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { PortfolioProject } from '../data/portfolioProjects';

interface ProjectMarkerProps {
  project: PortfolioProject;
  latitude: number;
  longitude: number;
  onClick: (project: PortfolioProject, position: THREE.Vector3) => void;
  onHover: (isHovered: boolean) => void;
}

export default function ProjectMarker({
  project,
  latitude,
  longitude,
  onClick,
  onHover,
}: ProjectMarkerProps) {
  const markerRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);

  // Convert lat/lon to 3D position on sphere
  const radius = 1; // Globe radius
  const phi = (90 - latitude) * (Math.PI / 180);
  const theta = (longitude + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);

  const position = new THREE.Vector3(x, y, z);

  // Calculate rotation to point outward from globe center
  // The pin should point away from the origin (0,0,0)
  const outwardDirection = position.clone().normalize();
  const quaternion = new THREE.Quaternion();
  
  // Create a rotation that points the pin outward
  // Default cylinder points up (0,1,0), we want it to point toward outwardDirection
  const up = new THREE.Vector3(0, 1, 0);
  quaternion.setFromUnitVectors(up, outwardDirection);

  // Gentle pulse animation
  useFrame((state) => {
    if (markerRef.current) {
      const scale = hovered ? 1.5 : 1;
      const pulse = Math.sin(state.clock.elapsedTime * 2) * 0.15 + 1;
      markerRef.current.scale.setScalar(scale * pulse * 0.25);
    }
  });

  const handleClick = () => {
    onClick(project, position);
  };

  const handlePointerOver = () => {
    setHovered(true);
    onHover(true);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = () => {
    setHovered(false);
    onHover(false);
    document.body.style.cursor = 'auto';
  };

  return (
    <group
      ref={markerRef}
      position={[x, y, z]}
      quaternion={quaternion}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      {/* Pin base */}
      <mesh position={[0, 0.25, 0]}>
        <cylinderGeometry args={[0.08, 0.08, 0.5, 8]} />
        <meshStandardMaterial color={project.color} emissive={project.color} emissiveIntensity={1} />
      </mesh>

      {/* Pin head */}
      <mesh position={[0, 0.55, 0]}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshStandardMaterial
          color={project.color}
          emissive={project.color}
          emissiveIntensity={hovered ? 2 : 1.5}
        />
      </mesh>

      {/* Glow effect */}
      <mesh position={[0, 0.55, 0]} scale={hovered ? 2.5 : 2}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshBasicMaterial
          color={project.color}
          transparent
          opacity={hovered ? 0.7 : 0.5}
        />
      </mesh>
      
      {/* Additional bright glow ring */}
      <mesh position={[0, 0.55, 0]} scale={hovered ? 3.5 : 3}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshBasicMaterial
          color={project.color}
          transparent
          opacity={hovered ? 0.4 : 0.3}
        />
      </mesh>
    </group>
  );
}
